/**
 * useViewerHotkeys - Centralized keyboard shortcuts for the viewer
 *
 * Shortcuts:
 * - Navigation: ← Previous, → Next, N Next Unlabeled
 * - Window Presets: Shift+1-5 or Shift+T/L/B/R/V (mnemonics)
 * - Sidebar Toggles: ',' for left, '.' for right
 * - Measurement Tools: Shift+U (ruler), Shift+M (rectangle ROI), Shift+E (bump/edge)
 * - Escape: Return to default mode
 * - Delete/Backspace: Delete selected annotation
 * - Shift+/: Open help dialog
 */

import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSetAtom, useAtomValue } from "jotai";
import { activeSlotAtom } from "../atoms/hangingProtocolAtoms";
import {
  leftSidebarCollapsedAtom,
  rightSidebarCollapsedAtom,
  focusModeAtom,
} from "../atoms/uiAtoms";
import { WindowPreset, WINDOW_PRESETS } from "../constants/windowPresets";
import {
  activeMeasurementToolAtom,
  annotationSaveCountAtom,
  clearAllMeasurementsConfirmAtom,
} from "../atoms/measurementAtoms";
import { measureModeAtom } from "../atoms/measureAtoms";
import { hangingProtocolAtom, hangingProtocolActionsAtom } from "../atoms/hangingProtocolAtoms";
import { getCornerstoneTools, getCornerstone } from "../utils/dicomLoader";
import { useWindowPresetHotkeys } from "./useWindowPresetHotkeys";
import { useSegmentationToolActions } from "./useSegmentationToolActions";
import {
  activeSegmentIndexAtom,
  isSegmentationActiveAtom,
  segmentationVolumeIdAtom,
  segmentationAssistAtom,
  bumpRadiusAtom,
  BUMP_RADII,
  requestAnnotationStartAtom,
  nnClickMarkersAtom,
  segEngineAtom,
  contourVersionAtom,
} from "../atoms/segmentationAtoms";
import { isV2ToolEnabled } from "../utils/viewportToolSetup";
import {
  hasPreview as hasRegionGrowPreview,
  rejectRegionGrowPreview,
  undoRegionGrowSeed,
  hasNnInteractiveSession,
  getMultiSessions,
  undoNnInteractive,
  undoFallbackPlane,
  rejectNnInteractive,
  refineNnInteractive,
  removeRegionGrowImageId,
  getRegionGrowImageIds,
  addRegionGrowImageId,
  type PlaneKey,
} from "../tools/RegionGrowTool";
import { removeBumpedImageId, getBumpedImageIds, addBumpedImageId } from "../tools/BumpContourTool";
import { showNnToast } from "../tools/nnInteractiveSession";
import { pushUndo, popUndo, getActiveScope } from "../tools/annotationUndo";
import { findHangingProtocolViewport } from "../utils/dicomLoader";
import { hoveredSurfaceAtom } from "../atoms/hoveredSurfaceAtom";
import { reportFocusAtom, getFocusedChipId } from "../atoms/reporterChipAtoms";
import { runCommand } from "../lib/commands";
import { focusedFindingIndexAtom } from "../atoms/findingsAtoms";
import { logger } from "../utils/logger";
import { isLoadedFromMask } from "../utils/annotationFlags";
import { PREDICTION_MASK_SHORTCUT } from "@/constants/viewerActions";

/** Derive plane from a Cornerstone viewport's camera orientation.
 *  Uses the view-plane normal to determine axial/coronal/sagittal
 *  rather than hardcoding slot→plane mappings. */
function getViewportPlane(viewport: any): PlaneKey {
  try {
    const camera = viewport?.getCamera?.();
    const vpn = camera?.viewPlaneNormal;
    if (vpn) {
      const absX = Math.abs(vpn[0]),
        absY = Math.abs(vpn[1]),
        absZ = Math.abs(vpn[2]);
      if (absZ >= absX && absZ >= absY) return "axial";
      if (absY >= absX && absY >= absZ) return "coronal";
      return "sagittal";
    }
  } catch (e) {
    console.warn("[getViewportPlane] camera orientation lookup failed:", e);
  }
  return "axial";
}

/** Resolve the plane for the active slot by finding its viewport. */
function getSlotPlane(slotId: string): PlaneKey {
  const vp = findHangingProtocolViewport(slotId);
  return vp ? getViewportPlane(vp) : "axial";
}

interface UseViewerHotkeysOptions {
  onPresetSelect: (preset: WindowPreset, center: number, width: number) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onNextUnlabeled?: () => void;
  onTogglePredictionMasks?: () => void;
  enabled?: boolean;
  /** Study has button parity; legacy retains auto-start; disabled is viewing only. */
  segmentationMode?: "study" | "legacy" | "disabled";
  segmentationEngineAvailable?: boolean;
}

export function useViewerHotkeys({
  onPresetSelect,
  onPrevious,
  onNext,
  onNextUnlabeled,
  onTogglePredictionMasks,
  enabled = true,
  segmentationMode = "legacy",
  segmentationEngineAvailable = true,
}: UseViewerHotkeysOptions) {
  const segmentationActions = useSegmentationToolActions({
    enabled: enabled && segmentationMode === "study",
    engineAvailable: segmentationEngineAvailable,
  });
  const setLeftSidebarCollapsed = useSetAtom(leftSidebarCollapsedAtom);
  const setRightSidebarCollapsed = useSetAtom(rightSidebarCollapsedAtom);
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  const annotationSavePending = useAtomValue(annotationSaveCountAtom) > 0;
  const setActiveMeasurementTool = useSetAtom(activeMeasurementToolAtom);
  const focusMode = useAtomValue(focusModeAtom);
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  const isSegmentationActive = useAtomValue(isSegmentationActiveAtom);
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const setBumpRadius = useSetAtom(bumpRadiusAtom);
  const segAssistConfig = useAtomValue(segmentationAssistAtom);
  const setRequestAnnotationStart = useSetAtom(requestAnnotationStartAtom);
  const setNnClickMarkers = useSetAtom(nnClickMarkersAtom);
  const setSegEngine = useSetAtom(segEngineAtom);
  const setFocusMode = useSetAtom(focusModeAtom);
  const hangingProtocolState = useAtomValue(hangingProtocolAtom);
  const dispatchHangingProtocol = useSetAtom(hangingProtocolActionsAtom);
  const activeSlot = useAtomValue(activeSlotAtom);
  const focusedFindingIndex = useAtomValue(focusedFindingIndexAtom);
  const setFocusedFindingIndex = useSetAtom(focusedFindingIndexAtom);
  const setContourVersion = useSetAtom(contourVersionAtom);
  const setClearAllMeasurementsConfirm = useSetAtom(clearAllMeasurementsConfirmAtom);
  // AI click-to-measure mode (entered from a report chip's 📐 button — see
  // measureAtoms.ts). Non-null = the CaseViewer banner is showing and the
  // viewer is waiting for a click. Read here (not gated on hoveredSurface)
  // so Escape can exit it below regardless of mouse position or focus.
  const measureMode = useAtomValue(measureModeAtom);
  // Plain-key viewer hotkeys (single letters, digits, arrows, punctuation)
  // are silenced while the rad's mouse is over the report editor — this is
  // what stops `b`, `n`, `,`, `.`, `←/→`, `shift+1..5`, `shift+u/m/s/e/g`,
  // `shift+t/l/b/r/v`, etc. from firing when the rad is dictating into the
  // report. Modifier-bound viewer hotkeys (`mod+z`) ignore this gate.
  const hoveredSurface = useAtomValue(hoveredSurfaceAtom);
  const plainKeyEnabled = enabled && !annotationSavePending && hoveredSurface !== "editor";
  // Universal-Tab workflow: rad mouse-scrolls the viewer, Tabs onto a chip,
  // hits Backspace to delete it. With plainKeyEnabled gating only on
  // hovered surface, the viewer's measurement-delete hotkey would fire
  // first (mouse is over the viewer) and the chip never gets the keystroke.
  // Backspace cedes to the chip when the report-focus SSoT shows a chip in
  // either Tab-focused or click-into-chip mode. Delete stays on the viewer
  // (preserves muscle memory for measurement delete via the Delete key).
  const reportFocus = useAtomValue(reportFocusAtom);
  const chipBackspaceWins = getFocusedChipId(reportFocus) !== null;
  // Cmd/Ctrl+Z cedes to the report editor's undo whenever the rad is working
  // in the report — focus is in a chip/prose, so reportFocusAtom is non-"none"
  // (the editor seeds caret-in-prose on every selectionUpdate, including the
  // refocus after a right-click menu action). Never gate on mouse hover:
  // hoveredSurfaceAtom's doc says modifier hotkeys must not depend on it, and
  // a portaled context menu nulls the hover mid-action — which stranded this
  // gate and let the viewer's annotation-undo (below) swallow the keystroke.
  const reportUndoWins = reportFocus.mode !== "none";

  // Window preset shortcuts (Shift+1-5 and Shift+T/L/B/R/V)
  useWindowPresetHotkeys({ onPresetSelect, enabled });

  // CE/form flags below: the mouse-hover gate already silences these
  // when the rad's pointer is over the editor (where the keystroke is
  // intended as text input). When mouse is over the viewer with focus
  // still in the editor (the rad's dictation-while-scrolling workflow),
  // the binding must still fire — hence enableOnContentEditable. Form tags
  // (note boxes, text inputs, selects) never take plain keys: what a reader
  // types there is text, and react-hotkeys-hook preventDefaults a form-tag
  // match before the callback runs, so `n` or `→` in a note would change case.
  const plainKeyOpts = {
    enabled: plainKeyEnabled,
    preventDefault: true,
    enableOnContentEditable: true,
    enableOnFormTags: false,
  } as const;

  useHotkeys(
    PREDICTION_MASK_SHORTCUT,
    (event) => {
      if (!event.repeat) onTogglePredictionMasks?.();
    },
    {
      ...plainKeyOpts,
      enabled: plainKeyEnabled && !!onTogglePredictionMasks,
      enableOnContentEditable: false,
    },
    [onTogglePredictionMasks],
  );

  // Bare-key preset shortcut: B for Bone (matches HelpDialog "Shift+3 / B")
  useHotkeys(
    "b",
    () => {
      const bone = WINDOW_PRESETS.find((p) => p.id === "bone")!;
      onPresetSelect(bone.id, bone.center, bone.width);
    },
    plainKeyOpts,
  );

  // Sidebar toggle: ',' for left sidebar
  useHotkeys(
    "comma",
    () => {
      setLeftSidebarCollapsed((prev) => !prev);
    },
    plainKeyOpts,
  );

  // Sidebar toggle: '.' for right sidebar
  useHotkeys(
    "period",
    () => {
      setRightSidebarCollapsed((prev) => !prev);
    },
    plainKeyOpts,
  );

  // Navigation: ← Previous case
  useHotkeys(
    "left",
    () => {
      onPrevious?.();
    },
    { ...plainKeyOpts, enabled: plainKeyEnabled && !!onPrevious },
    [onPrevious],
  );

  // Navigation: → Next case
  useHotkeys(
    "right",
    () => {
      onNext?.();
    },
    { ...plainKeyOpts, enabled: plainKeyEnabled && !!onNext },
    [onNext],
  );

  // Navigation: N Next unlabeled case
  useHotkeys(
    "n",
    () => {
      onNextUnlabeled?.();
    },
    { ...plainKeyOpts, enabled: plainKeyEnabled && !!onNextUnlabeled },
    [onNextUnlabeled],
  );

  // Measurement tool: Shift+U for rUler (LengthTool)
  useHotkeys("shift+u", () => runCommand("setActiveTool", { tool: "length" }), plainKeyOpts);

  // Measurement tool: Shift+M for Mark rectangle (RectangleROITool)
  useHotkeys("shift+m", () => runCommand("setActiveTool", { tool: "rectangleROI" }), plainKeyOpts);

  // Segmentation tool: Shift+S for Segmentation lasso (PlanarFreehandContourSegmentationTool)
  // Auto-starts annotation if not already active. Hover-gated so a capital
  // S typed during dictation post-process output stays as text.
  useHotkeys(
    "shift+s",
    (e) => {
      e.preventDefault();
      if (segmentationMode === "disabled") return;
      if (segmentationMode === "study") return segmentationActions.selectTool("lasso");
      if (!segmentationVolumeId) return; // viewport still loading
      if (!isSegmentationActive) {
        setRequestAnnotationStart("lasso");
        return;
      }
      // Lasso/Bump are explicit segmentation modes. Unlike the general
      // setActiveTool command, a repeated Shift+S must not toggle lasso off
      // after a completed stroke.
      setActiveMeasurementTool("lasso");
    },
    plainKeyOpts,
    [
      isSegmentationActive,
      segmentationVolumeId,
      setActiveMeasurementTool,
      segmentationMode,
      segmentationActions,
    ],
  );

  // Segmentation tool: Shift+E for Edge/bump contour refinement tool
  // Auto-starts annotation if not already active
  useHotkeys(
    "shift+e",
    (e) => {
      e.preventDefault();
      if (segmentationMode === "disabled") return;
      if (segmentationMode === "study") return segmentationActions.selectTool("bump");
      if (!segmentationVolumeId) return; // viewport still loading
      if (!isSegmentationActive) {
        setRequestAnnotationStart("bump");
        return;
      }
      setActiveMeasurementTool("bump");
    },
    plainKeyOpts,
    [
      isSegmentationActive,
      segmentationVolumeId,
      setActiveMeasurementTool,
      segmentationMode,
      segmentationActions,
    ],
  );

  // Segmentation tool: Shift+G for region Grow (V2 S3 one-click flood-fill)
  // Auto-starts annotation if not already active.
  // If all contours were deleted, clear the stale nnInteractive session
  // so the next click starts fresh instead of trying to refine.
  useHotkeys(
    "shift+g",
    (e) => {
      e.preventDefault();
      if (segmentationMode === "disabled") return;
      if (segmentationMode === "study") {
        if (!e.repeat) segmentationActions.toggleRegionGrow();
        return;
      }
      if (!segmentationVolumeId) return; // viewport still loading
      if (!isSegmentationActive) {
        setRequestAnnotationStart("regionGrow");
        return;
      }
      if (!isV2ToolEnabled(segAssistConfig, "region_grow")) return;
      // Clear stale nnInteractive session if all contours were deleted
      if (hasNnInteractiveSession()) {
        const sessions = getMultiSessions();
        const hasContours = Array.from(sessions.values()).some((s) => s.contourUIDs.length > 0);
        if (!hasContours) {
          sessions.clear();
        }
      }
      // setActiveTool restores the previousDrawingTool when toggled off, so
      // regionGrow → regionGrow lands the rad back on whatever drawing tool
      // they used last (typically lasso). Going through runCommand keeps
      // previousDrawingToolAtom bookkeeping symmetric with Shift+L/U/M.
      runCommand("setActiveTool", {
        tool: activeMeasurementTool === "regionGrow" ? "lasso" : "regionGrow",
      });
    },
    plainKeyOpts,
    [
      activeMeasurementTool,
      isSegmentationActive,
      segAssistConfig,
      segmentationVolumeId,
      segmentationMode,
      segmentationActions,
    ],
  );

  // Shift+I — toggle segmentation engine between nnInteractive and local threshold.
  useHotkeys(
    "shift+i",
    (e) => {
      e.preventDefault();
      if (segmentationMode === "disabled") return;
      if (segmentationMode === "study") {
        if (!e.repeat) segmentationActions.toggleEngine();
        return;
      }
      setSegEngine((prev) => {
        const next = prev === "nnInteractive" ? "threshold" : "nnInteractive";
        showNnToast(
          next === "nnInteractive" ? "Segmentation: AI Segmentation" : "Segmentation: Threshold",
        );
        return next;
      });
    },
    segmentationMode === "legacy" ? { enabled, preventDefault: true } : plainKeyOpts,
    [segmentationMode, segmentationActions],
  );

  // Bump radius: [ to decrease, ] to increase (cycles through BUMP_RADII)
  useHotkeys(
    "bracketleft",
    () => {
      setBumpRadius((prev) => {
        const idx = BUMP_RADII.indexOf(prev as (typeof BUMP_RADII)[number]);
        return BUMP_RADII[Math.max(0, idx - 1)] as typeof prev;
      });
    },
    plainKeyOpts,
  );

  useHotkeys(
    "bracketright",
    () => {
      setBumpRadius((prev) => {
        const idx = BUMP_RADII.indexOf(prev as (typeof BUMP_RADII)[number]);
        return BUMP_RADII[Math.min(BUMP_RADII.length - 1, idx + 1)] as typeof prev;
      });
    },
    plainKeyOpts,
  );

  // NOTE: Shift+F (fill/interpolate) has been removed — interpolation is now automatic.
  // When `interpolation: { enabled: true }` is set on the tool config, Cornerstone
  // auto-generates interpolated contours between keyframe slices. The viewer
  // auto-accepts them via the ANNOTATION_COMPLETED event listener.

  // Ctrl+Z: undo last annotation action
  // - Region grow mode: undo last seed/click (removes contour + propagated slices)
  // - Lasso mode: remove the last drawn contour on the current slice
  useHotkeys(
    "mod+z",
    (e) => {
      // LabelingPage and its embedded CaseViewer both mount this hook.
      // Claim a segmentation Undo event before either queue or fallback work,
      // so the second handler cannot undo the next action on the same keypress.
      if (isSegmentationActive) {
        if (e.defaultPrevented) return;
        e.preventDefault();
      }
      try {
        const csTools = getCornerstoneTools();

        // UNIFIED UNDO QUEUE — checked FIRST so Ctrl+Z reverts the MOST-RECENT
        // committed action regardless of tool (lasso, bump, delete, and
        // nnInteractive seed/refine, which now each push one entry). popUndo
        // returns them newest-first, so a bump done after an nn seed undoes the
        // BUMP first (fixing the old fixed-priority cascade). Scope to the active
        // session (null when segmentation is inactive / in the demo viewer) so a
        // stale entry from a finished lesion can't apply. The tool-specific
        // branches below now run only as fallbacks for state the queue doesn't
        // hold (e.g. an in-progress region-grow PREVIEW).
        const loggedUndo = popUndo(isSegmentationActive ? getActiveScope() : null);
        if (loggedUndo) {
          loggedUndo.undo();
          // After undoing the final nn step, re-enable region grow so the user
          // can start fresh without a manual tool switch (preserves the prior
          // nn-undo UX now that nn routes through the queue).
          if (
            loggedUndo.kind === "nnRevert" &&
            !hasNnInteractiveSession() &&
            isV2ToolEnabled(segAssistConfig, "region_grow")
          ) {
            runCommand("setActiveTool", { tool: "regionGrow" });
          }
          setContourVersion((v) => v + 1);
          getCornerstone()
            .getRenderingEngines?.()
            ?.forEach((engine: any) => {
              try {
                engine.render();
              } catch (err) {
                logger.warn("[useViewerHotkeys] post-undo render failed:", err);
              }
            });
          e.preventDefault();
          return;
        }

        // Segmentation has one chronological history. Exhausting it must never
        // fall through to deleting a baseline contour or an older tool session.
        if (isSegmentationActive) return;

        // nnInteractive: undo last refinement on the active viewport
        if (hasNnInteractiveSession()) {
          const activePlane = getSlotPlane(activeSlot);
          if (undoNnInteractive(csTools, activePlane)) {
            e.preventDefault();
            // If all sessions are now empty, re-enable region grow tool
            // so the user can start a fresh annotation without manual tool switch.
            if (!hasNnInteractiveSession() && isV2ToolEnabled(segAssistConfig, "region_grow")) {
              runCommand("setActiveTool", { tool: "regionGrow" });
            }
            return;
          }

          // Fallback mode: per-plane undo. Strip the active viewport's
          // contour + propagation only, leave the other planes alone so
          // each Ctrl+Z clears one view at a time.
          if (undoFallbackPlane(csTools, activePlane)) {
            e.preventDefault();
            if (!hasNnInteractiveSession() && isV2ToolEnabled(segAssistConfig, "region_grow")) {
              runCommand("setActiveTool", { tool: "regionGrow" });
            }
            return;
          }
        }

        // Region grow (local) or fallback companion: undo last seed or reject
        // entire preview. Don't gate on activeMeasurementTool — after a region
        // grow click the tool auto-switches to bump, which would block undo
        // in fallback mode where there's still an unaccepted preview to clear.
        if (hasRegionGrowPreview()) {
          if (undoRegionGrowSeed(csTools)) {
            e.preventDefault();
            return;
          }
        }

        // Lasso/any mode: remove the most recent contour on the CURRENT viewport
        // slice. Scope to the focused slice's referencedImageId (mirrors the
        // Delete handler) so undo can't remove a contour on a different slice
        // ("contours.length-1" is array order across ALL slices, not the slice
        // in view). When the current imageId is unknown, fall back to global
        // last so undo still works rather than no-op'ing.
        const undoVp = findHangingProtocolViewport(activeSlot);
        const undoCurrentImageId = undoVp?.getCurrentImageId?.() ?? null;
        const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
        const contours = allAnns.filter(
          (a: any) =>
            a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            a.data?.segmentation?.segmentIndex === activeSegmentIndex &&
            !isLoadedFromMask(a) &&
            a.data?.contour?.polyline?.length >= 3 &&
            (!undoCurrentImageId || a.metadata?.referencedImageId === undoCurrentImageId),
        );
        if (contours.length > 0) {
          const last = contours[contours.length - 1];
          csTools.annotation.state.removeAnnotation(last.annotationUID);
          // Refresh canvas + the contourVersion-subscribed panel (the lasso
          // branch previously skipped this → stale "glitch" until the next
          // interaction). Mirrors the Delete handler.
          setContourVersion((v) => v + 1);
          getCornerstone()
            .getRenderingEngines?.()
            ?.forEach((engine: any) => {
              try {
                engine.render();
              } catch (err) {
                logger.warn("[useViewerHotkeys] post-undo render failed:", err);
              }
            });
          e.preventDefault();
        }
      } catch (err) {
        console.error("[useViewerHotkeys] mod+z undo failed:", err);
      }
    },
    {
      enabled: enabled && !annotationSavePending && !reportUndoWins,
      preventDefault: false,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    // isSegmentationActive is read above to scope popUndo — keep it in deps so
    // the handler re-registers with the current value (stale-closure safety).
    [
      activeMeasurementTool,
      activeSegmentIndex,
      activeSlot,
      setContourVersion,
      isSegmentationActive,
    ],
  );

  // Measure-mode Escape: click-to-measure (measureModeAtom) is a separate
  // modal state with a visible banner (CaseViewer) whose Cancel/Done button
  // was, until now, the ONLY way to exit it — no keyboard path existed.
  // Unlike plainKeyOpts, this must NOT gate on hoveredSurfaceAtom (the banner
  // is modal regardless of where the mouse is) and must fire even with focus
  // in the report's contentEditable, since the rad may hit Escape mid-dictation.
  useHotkeys(
    "escape",
    () => {
      runCommand("exitMeasureMode", {});
    },
    {
      enabled: enabled && measureMode !== null,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [measureMode],
  );

  // Escape priority: focus/maximize exit first, then annotation reject, then clear tool.
  // This ensures ESC always exits view modes before destroying annotation state.
  useHotkeys(
    "escape",
    () => {
      // Measure mode owns this keypress (handled by the binding above).
      // react-hotkeys-hook fires every enabled "escape" binding on the same
      // keydown, so without this guard one Esc press in measure mode would
      // ALSO clear the active tool / exit maximize / etc below. measureMode
      // is read from render scope, which is stale-but-correct for the
      // keydown that triggered this render.
      if (measureMode !== null) return;
      // Priority 0: Exit Tab-cycles-findings mode so subsequent Tab presses
      // resume viewport cycling. Goes ahead of focus/maximize because the
      // finding-cursor is the narrowest mode and the rad will hit Escape to
      // exit it most often.
      if (focusedFindingIndex !== null) {
        setFocusedFindingIndex(null);
        return;
      }
      // Priority 1: Exit focus mode if active
      if (focusMode.enabled) {
        setFocusMode({ enabled: false, slotId: null });
        return;
      }
      // Priority 2: Exit maximize mode if active
      if (hangingProtocolState.maximizedSlot !== null) {
        dispatchHangingProtocol({
          type: "TOGGLE_MAXIMIZE",
          slotId: hangingProtocolState.maximizedSlot,
        });
        return;
      }
      // Priority 3: Reject nnInteractive session if active (works from any tool)
      if (hasNnInteractiveSession()) {
        try {
          const csTools = getCornerstoneTools();
          rejectNnInteractive(csTools);
        } catch {
          /* ignore */
        }
        return;
      }
      // Priority 4: Reject region grow preview (local) if active
      if (activeMeasurementTool === "regionGrow" && hasRegionGrowPreview()) {
        try {
          const csTools = getCornerstoneTools();
          rejectRegionGrowPreview(csTools);
        } catch {
          /* ignore */
        }
        return;
      }
      // Priority 5: Clear measurement tool. Through runCommand so the
      // previousDrawingToolAtom is cleared in lockstep — leaving it set
      // would resurrect the prior drawing tool on the next W/H press.
      runCommand("setActiveTool", { tool: "none" });
    },
    plainKeyOpts,
    [
      measureMode,
      focusedFindingIndex,
      setFocusedFindingIndex,
      focusMode.enabled,
      hangingProtocolState.maximizedSlot,
      activeMeasurementTool,
    ],
  );

  // Delete / Backspace — single-slice delete of the in-progress annotation
  // on the focused viewport's current slice.
  //
  // Guards:
  // - `isSegmentationActive` gate keeps saved/loaded masks safe: when the
  //   rad isn't actively annotating, Delete falls through to the measurement
  //   path. Saved annotations are removed via the right-panel delete button
  //   (handleDelete in SegmentationAnnotationPanel), not this hotkey.
  // - `_loadedFromMask` filter is the per-annotation belt to the gate's
  //   suspenders — even if the user starts a fresh annotation and somehow
  //   the segmentIndex collides with a loaded mask, we never touch contours
  //   that came from the persisted NIfTI.
  //
  // Backspace + Delete share this handler but bind separately below:
  // Backspace is also gated on chipBackspaceWins so the rad can delete a
  // Tab-focused chip without moving the mouse off the viewer. Delete is
  // not — measurement delete via Delete stays universal.
  const handleAnnotationDelete = useCallback(() => {
    try {
      const cornerstone = getCornerstone();
      const cornerstoneTools = getCornerstoneTools();

      if (!segmentationVolumeId || !isSegmentationActive) {
        // Not actively segmenting → delete the selected measurement (ruler /
        // ROI). Gate on isSegmentationActive, NOT on whether a seg volume is
        // mounted: the labeling viewer mounts a seg volume for the study even
        // while the rad is only measuring, and the old `if (!segmentationVolumeId)`
        // here made Delete a no-op in exactly that state. deleteSelectedAnnotation
        // is itself scoped to measurement tools, so a stray contour selection is
        // never removed by this path.
        runCommand("deleteSelectedAnnotation", {});
        return;
      }

      const activeVp = findHangingProtocolViewport(activeSlot);
      const currentImageId = activeVp?.getCurrentImageId?.() ?? null;
      // Safety: without a known current slice we'd risk wiping every slice.
      // Bail rather than silently deleting too much.
      if (!currentImageId) {
        logger.warn("[useViewerHotkeys] delete skipped: focused viewport has no current imageId");
        return;
      }

      const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
      const toRemove = allAnnotations.filter(
        (a: any) =>
          a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          !isLoadedFromMask(a) &&
          !a.isLocked &&
          (a.data?.segmentation?.segmentIndex === activeSegmentIndex ||
            a.data?.segmentIndex === activeSegmentIndex) &&
          a.metadata?.referencedImageId === currentImageId,
      );
      if (toRemove.length === 0) return;
      // Capture for undo BEFORE removal — removeAnnotation only unregisters the
      // annotation from state, so the objects stay valid to re-add on Ctrl+Z.
      // The viewport element is a valid AnnotationGroupSelector for re-add.
      const removedForUndo = toRemove.slice();
      const restoreSelector = activeVp?.element;
      // Capture per-imageId tracking membership BEFORE the prune below so the
      // undo closure can restore it. Without this, delete -> Ctrl+Z -> save
      // mis-counts assist metrics: a restored bumped contour drops out of
      // bumped_slices, and a restored region-grow contour both undercounts
      // region_grow_slices AND flips to interpolatedSlices — SegmentationAnnotationPanel
      // keys isRegionGrow / bumped on exactly these sets (the polyline itself is
      // correct either way; this is attribution, not ground truth).
      const wasRegionGrowSlice = getRegionGrowImageIds().has(currentImageId);
      const wasBumpedSlice = getBumpedImageIds().has(currentImageId);
      toRemove.forEach((a: any) => {
        cornerstoneTools.annotation.state.removeAnnotation(a.annotationUID);
      });
      pushUndo({
        kind: "deleteRestore",
        undo: () => {
          for (const a of removedForUndo) {
            try {
              cornerstoneTools.annotation.state.addAnnotation(a, restoreSelector);
            } catch (err) {
              logger.warn("[useViewerHotkeys] delete-undo re-add failed:", err);
            }
          }
          // True inverse of the prune below: re-add the imageId to whichever
          // tracking sets it was in, so the re-added contour counts as
          // region-grow / bumped again on the next save.
          if (wasRegionGrowSlice) addRegionGrowImageId(currentImageId);
          if (wasBumpedSlice) addBumpedImageId(currentImageId);
        },
      });

      // Prune per-imageId tracking so the save payload's
      // region_grow_slices / bumped_slices counts stay accurate.
      removeRegionGrowImageId(currentImageId);
      removeBumpedImageId(currentImageId);

      // Clean up nnInteractive session if no contours remain anywhere.
      // Lets Shift+G start a fresh region grow and clears stale markers.
      if (hasNnInteractiveSession()) {
        const sessions = getMultiSessions();
        let anyContoursLeft = false;
        sessions.forEach((session) => {
          session.contourUIDs = session.contourUIDs.filter((uid) => {
            try {
              return !!cornerstoneTools.annotation.state.getAnnotation(uid);
            } catch (e) {
              logger.warn("[useViewerHotkeys] nn session uid lookup failed:", e);
              return false;
            }
          });
          if (session.contourUIDs.length > 0) anyContoursLeft = true;
        });
        if (!anyContoursLeft) {
          sessions.clear();
          setNnClickMarkers([]);
        }
      }

      setContourVersion((v) => v + 1);
      cornerstone.getRenderingEngines()?.forEach((engine: any) => {
        try {
          engine.render();
        } catch (e) {
          logger.warn("[useViewerHotkeys] post-delete render failed:", e);
        }
      });
    } catch (err) {
      logger.error("[useViewerHotkeys] delete/backspace handler failed:", err);
    }
  }, [
    activeMeasurementTool,
    segmentationVolumeId,
    isSegmentationActive,
    activeSegmentIndex,
    activeSlot,
  ]);

  useHotkeys("delete", handleAnnotationDelete, plainKeyOpts, [handleAnnotationDelete]);
  useHotkeys(
    "backspace",
    handleAnnotationDelete,
    { ...plainKeyOpts, enabled: plainKeyEnabled && !chipBackspaceWins },
    [handleAnnotationDelete, chipBackspaceWins],
  );

  // Shift+Delete — wipe ALL slices of the in-progress annotation on the
  // focused viewport. Companion viewports keep their contours; saved
  // annotations are untouched (gated on isSegmentationActive and the
  // _loadedFromMask / isLocked exclusions).
  //
  // In MEASUREMENT context (no active segmentation) this combo used to no-op;
  // it now clears every ruler/ROI on the CURRENT slice — the per-slice middle
  // ground between "delete the one selected" and "clear the whole study".
  useHotkeys(
    "shift+delete, shift+backspace",
    () => {
      try {
        if (!segmentationVolumeId || !isSegmentationActive) {
          const measVp = findHangingProtocolViewport(activeSlot);
          const currentImageId = measVp?.getCurrentImageId?.() ?? null;
          if (!currentImageId) {
            logger.warn(
              "[useViewerHotkeys] clear-slice skipped: focused viewport has no current imageId",
            );
            return;
          }
          runCommand("clearMeasurementsOnSlice", { imageId: currentImageId });
          return;
        }

        const cornerstone = getCornerstone();
        const cornerstoneTools = getCornerstoneTools();

        const activeVp = findHangingProtocolViewport(activeSlot);
        if (!activeVp) {
          logger.warn("[useViewerHotkeys] shift+delete skipped: no focused viewport");
          return;
        }
        const focusedImageIds: string[] = activeVp.getImageIds?.() ?? [];
        if (focusedImageIds.length === 0) {
          logger.warn("[useViewerHotkeys] shift+delete skipped: focused viewport has no imageIds");
          return;
        }
        const focusedSet = new Set(focusedImageIds);

        const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
        const toRemove = allAnnotations.filter(
          (a: any) =>
            a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            !isLoadedFromMask(a) &&
            !a.isLocked &&
            (a.data?.segmentation?.segmentIndex === activeSegmentIndex ||
              a.data?.segmentIndex === activeSegmentIndex) &&
            focusedSet.has(a.metadata?.referencedImageId),
        );
        if (toRemove.length === 0) return;

        const removedImageIds = new Set<string>();
        toRemove.forEach((a: any) => {
          const refId: string | undefined = a.metadata?.referencedImageId;
          if (refId) removedImageIds.add(refId);
          cornerstoneTools.annotation.state.removeAnnotation(a.annotationUID);
        });
        removedImageIds.forEach((id) => {
          removeRegionGrowImageId(id);
          removeBumpedImageId(id);
        });

        // Same nnInteractive cleanup as the per-slice path — a viewport-wide
        // wipe is the more common trigger for "no contours left".
        if (hasNnInteractiveSession()) {
          const sessions = getMultiSessions();
          let anyContoursLeft = false;
          sessions.forEach((session) => {
            session.contourUIDs = session.contourUIDs.filter((uid) => {
              try {
                return !!cornerstoneTools.annotation.state.getAnnotation(uid);
              } catch (e) {
                logger.warn("[useViewerHotkeys] nn session uid lookup failed:", e);
                return false;
              }
            });
            if (session.contourUIDs.length > 0) anyContoursLeft = true;
          });
          if (!anyContoursLeft) {
            sessions.clear();
            setNnClickMarkers([]);
          }
        }

        setContourVersion((v) => v + 1);
        cornerstone.getRenderingEngines()?.forEach((engine: any) => {
          try {
            engine.render();
          } catch (e) {
            logger.warn("[useViewerHotkeys] post-wipe render failed:", e);
          }
        });
      } catch (err) {
        logger.error("[useViewerHotkeys] shift+delete handler failed:", err);
      }
    },
    plainKeyOpts,
    [segmentationVolumeId, isSegmentationActive, activeSegmentIndex, activeSlot],
  );

  // Ctrl/Cmd+Shift+Backspace — clear ALL measurements (ruler + ROI) across the
  // study. Opens the confirm dialog first (study-wide + not undoable). The
  // command it ultimately runs touches measurement annotations only, so this is
  // safe to bind regardless of segmentation state. `Backspace` (not `Delete`)
  // avoids Chrome's Ctrl+Shift+Delete clear-browsing-data combo.
  useHotkeys("mod+shift+backspace", () => setClearAllMeasurementsConfirm(true), plainKeyOpts, [
    setClearAllMeasurementsConfirm,
  ]);

  // Shift+/ help dialog hotkey is now global (see App.tsx)

  // ─── Global Shift+Click / Ctrl+Click interceptor for nn refinement ──────
  // Works regardless of which Cornerstone tool is active (zoom, pan, bump, etc.)
  useEffect(() => {
    if (!enabled || segmentationMode === "disabled") return;

    const handleMouseDown = (e: MouseEvent) => {
      // Only intercept Shift+Click (positive) or Ctrl+Click (negative)
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) return;
      // Don't intercept if no nn session
      if (!hasNnInteractiveSession()) return;
      // Must be on a canvas element (Cornerstone viewport)
      const target = e.target as HTMLElement;
      if (target.tagName !== "CANVAS") return;

      const label: "positive" | "negative" = e.ctrlKey || e.metaKey ? "negative" : "positive";

      // Find which viewport this canvas belongs to
      try {
        const cs = getCornerstone();
        const viewport = cs
          .getEnabledElements?.()
          ?.map((el: any) => el.viewport)
          ?.find((vp: any) => vp?.element?.contains(target));
        if (!viewport) return;

        // Derive plane from the viewport's camera orientation (not hardcoded slot mapping)
        const clickedPlane = getViewportPlane(viewport);

        // Convert canvas coords to world
        const rect = target.getBoundingClientRect();
        const canvasPoint: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
        const worldPoint = viewport.canvasToWorld(canvasPoint);
        if (!worldPoint) return;

        // Attempt refinement
        if (refineNnInteractive(worldPoint, clickedPlane, label)) {
          e.preventDefault();
          e.stopPropagation();
        }
      } catch {
        /* Cornerstone not ready */
      }
    };

    // Capture phase so we intercept before Cornerstone tools
    document.addEventListener("mousedown", handleMouseDown, true);
    return () => document.removeEventListener("mousedown", handleMouseDown, true);
  }, [enabled, segmentationMode]);

  // Return current measurement tool state for UI indicators if needed
  return { activeMeasurementTool };
}

// Re-export from shared constants for backwards compatibility
export { WINDOW_PRESETS as PRESET_HOTKEYS } from "../constants/windowPresets";
